/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 */
import {productUrlBuilder} from '@amblique/king-living/app/utils/url';
import {getMasterTileImages} from '@amblique/king-living/app/utils/product-images';
import {
  CYLINDO_SIZE,
  normalizePlainTileImageSrc,
} from '@amblique/king-living/app/utils/responsive-image-src';
import {boldString, capitalize} from '@salesforce/retail-react-app/app/utils/utils';

import {
  getIdleBlogImageUrl,
  mapPageToSpotlightBlog,
} from '@amblique/king-living/app/components/search/search-spotlight-blog-helpers';

/** Refine to product-ish hit types only for product-only totals and rows. */
export const SEARCH_TYPEAHEAD_PRODUCT_HTYPE_REFINE = 'htype=master|product|variant|bundle|set|variation_group';

export const SEARCH_TYPEAHEAD_MAX_MOBILE = 7;
export const SEARCH_TYPEAHEAD_MAX_DESKTOP = 8;

/** Blog rows reserved at top of merged typeahead list (then products fill remaining slots). */
export const SEARCH_TYPEAHEAD_MAX_BLOGS_MOBILE = 2;
export const SEARCH_TYPEAHEAD_MAX_BLOGS_DESKTOP = 3;

/**
 * @param {object} hit - productSearch hit
 * @returns {boolean}
 */
export function isArticleSearchHit(hit) {
  if (!hit || typeof hit !== 'object') {
    return false;
  }

  const ht = String(hit.hitType || '').toLowerCase();

  if (ht === 'content' || ht === 'article') {
    return true;
  }

  const customType = String(hit.c_hitType || hit.c_searchHitType || '').toLowerCase();

  if (customType === 'content' || customType === 'article' || customType === 'blog') {
    return true;
  }

  const pageUrl = String(hit.c_productPageURL || hit.c_pageUrl || hit.c_pwaUrl || '');

  if (/(^|\/)blog(\/|$)/i.test(pageUrl) || /article/i.test(pageUrl)) {
    return true;
  }

  return false;
}

/**
 * @param {object} hit
 * @returns {string}
 */
export function buildTypeaheadHitLink(hit) {
  if (!hit) {
    return '';
  }

  const explicit =
    hit.c_articlePageURL ||
    hit.c_contentPageURL ||
    hit.c_blogPageURL ||
    hit.c_pageUrl ||
    hit.c_pwaUrl;

  if (typeof explicit === 'string' && explicit.trim()) {
    const t = explicit.trim();

    if (/^https?:\/\//i.test(t)) {
      return t;
    }

    return t.startsWith('/') ? t : `/${t}`;
  }

  return productUrlBuilder(hit);
}

/**
 * @param {object} hit
 * @returns {string|null}
 */
export function getTypeaheadHitImageUrl(hit) {
  const fromMaster = getMasterTileImages(hit).find(
    (img) => img.mediaType !== 'video' && (img.disBaseLink || img.link)
  );

  /** @type {string|null} */
  let raw = null;

  if (fromMaster?.disBaseLink || fromMaster?.link) {
    raw = fromMaster.disBaseLink || fromMaster.link || null;
  } else if (hit?.image?.disBaseLink || hit?.image?.link) {
    raw = hit.image.disBaseLink || hit.image.link || null;
  } else {
    const rp = hit?.representedProduct;
    const rpImg = rp?.image;

    if (rpImg?.disBaseLink || rpImg?.link) {
      raw = rpImg.disBaseLink || rpImg.link || null;
    }
  }

  if (!raw) {
    return null;
  }

  const normalized = normalizePlainTileImageSrc(raw, {cylindoSize: CYLINDO_SIZE.thumbLarge});

  return normalized || null;
}

/**
 * @param {object} hit - productSearch hit
 * @returns {string}
 */
export function getSearchHitPrimaryCategoryName(hit) {
  if (!hit || typeof hit !== 'object') {
    return '';
  }

  const raw =
    hit.c_primaryCategoryName ??
    hit.representedProduct?.c_primaryCategoryName ??
    '';

  return String(raw).trim();
}

/**
 * @param {object[]} hits
 * @param {string} query
 * @returns {Array<{key: string, kind: 'product', titleHtml: string, tag: string, imageUrl: string|null, link: string, primaryCategoryName?: string}>}
 */
export function buildTypeaheadRowsFromHits(hits, query) {
  if (!Array.isArray(hits) || hits.length === 0) {
    return [];
  }

  const cap = capitalize(query);

  return hits
    .filter((hit) => !isArticleSearchHit(hit))
    .map((hit, idx) => {
      const name = hit.productName || hit.name || '';
      const titleHtml = boldString(String(name), cap);

      return {
        key: hit.productId || hit.uuid || hit.hitId || `hit-${idx}`,
        kind: 'product',
        titleHtml,
        tag: 'Product',
        imageUrl: getTypeaheadHitImageUrl(hit),
        link: buildTypeaheadHitLink(hit),
        primaryCategoryName: getSearchHitPrimaryCategoryName(hit),
        product: hit,
        productIndex: idx,
      };
    });
}

/**
 * Build typeahead rows from Contentful blog Pages (keyword filter applied server-side).
 *
 * @param {object[]} pages - `pageCollection.items` from GetBlogListingPages
 * @param {string} query
 * @returns {Array<{key: string, kind: 'blog', titleHtml: string, tag: string, imageUrl: string|null, link: string}>}
 */
export function buildTypeaheadRowsFromBlogPages(pages, query) {
  if (!Array.isArray(pages) || pages.length === 0) {
    return [];
  }

  const cap = capitalize(query);

  return pages
    .map((page, idx) => {
      const spotlight = mapPageToSpotlightBlog(page);

      if (!spotlight) {
        return null;
      }

      const link = String(spotlight.pageSlug || '').trim();

      if (!link) {
        return null;
      }

      const title = spotlight.pageTitle || spotlight.title || '';
      const titleHtml = boldString(String(title), cap);

      return {
        key: `blog-${spotlight.pageSysId || spotlight.sys?.id || idx}`,
        kind: 'blog',
        titleHtml,
        tag: 'Blog',
        imageUrl: getIdleBlogImageUrl(spotlight) || null,
        link,
      };
    })
    .filter(Boolean);
}

/**
 * Show blog matches first (capped), then product rows until the flyout list max.
 *
 * @param {object[]} blogRows
 * @param {object[]} productRows
 * @param {boolean} isMobile
 * @returns {object[]}
 */
export function mergeAndCapTypeaheadRows(blogRows, productRows, isMobile) {
  const max = isMobile ? SEARCH_TYPEAHEAD_MAX_MOBILE : SEARCH_TYPEAHEAD_MAX_DESKTOP;
  const maxBlogs = isMobile ? SEARCH_TYPEAHEAD_MAX_BLOGS_MOBILE : SEARCH_TYPEAHEAD_MAX_BLOGS_DESKTOP;

  const blogs = (Array.isArray(blogRows) ? blogRows : []).slice(0, maxBlogs);
  const budget = Math.max(0, max - blogs.length);
  const products = (Array.isArray(productRows) ? productRows : []).slice(0, budget);

  let merged = [...blogs, ...products];

  if (isMobile) {
    merged = merged.filter((r) => r.kind !== 'article');
  }

  return merged;
}

/**
 * @param {ReturnType<typeof buildTypeaheadRowsFromHits>} rows
 * @param {boolean} isMobile
 * @returns {typeof rows}
 */
export function filterAndCapTypeaheadRows(rows, isMobile) {
  const max = isMobile ? SEARCH_TYPEAHEAD_MAX_MOBILE : SEARCH_TYPEAHEAD_MAX_DESKTOP;
  let next = isMobile ? rows.filter((r) => r.kind !== 'article') : [...rows];

  if (next.length > max) {
    next = next.slice(0, max);
  }

  return next;
}

/**
 * @param {ReturnType<typeof mergeAndCapTypeaheadRows>} rows
 * @returns {number}
 */
export function countProductRowsInList(rows) {
  return rows.filter((r) => r.kind === 'product').length;
}
